import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  CalendarIcon,
  CameraIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MixerHorizontalIcon
} from '@radix-ui/react-icons';
import {
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Label,
  Tabs,
  Text
} from '@raystack/apsara';
import * as _ from 'lodash';
import moment from 'moment';
import useSWRMutation from 'swr/mutation';

import { apiClient } from '@src/clients';
import { BandsList } from '@src/components/BandlistModal';
import { Header } from '@src/components/Custom/Header';
import { ScrollView } from '@src/components/PixxelUIKit/ScrollView';
import * as Skeleton from '@src/components/PixxelUIKit/Skeleton';
import NewFilter from '@src/containers/Explore/subComponents/Filter/NewFilter';
import ScenesInfo from '@src/containers/Viewer/subComponents/Scenes/ScenesInfo/ScenesInfo';
import { IdentityContext } from '@src/contexts/identity/context';
import { Show } from '@src/hoc';
import useConstellations from '@src/hooks/useConstellation';
import { ReactComponent as NewFilterIcon } from '@src/icons/new-filter.svg';
import { setSelectedImages } from '@src/redux/_reducers/mapControl';
import useDebounce from '@src/use-debounce';
import { getFormattedUtcDateTime } from '@src/utils';
import { FetchImages } from '@src/utils/apis/LensApi';
import { getAngleValue } from '@src/utils/commonUtils';
import {
  ImageSearchHelper,
  LensErrorHelper,
  RunImageSearch
} from '@src/utils/helpers/LensHelpers';

import { NewTileView } from './Views/NewTileView/NewTileView';
import { ReactComponent as CloudIcon } from './Views/NewTileView/cloud.svg';

import styles from './ImageList.module.css';

const TABS = { SELECTED: 'selected', ALL: 'all' };

const sortOptions = {
  date: {
    name: 'Date',
    icon: CalendarIcon
  },
  satellite: {
    name: 'Satellite',
    icon: CameraIcon
  },
  cloud: {
    name: 'Cloud Coverage',
    icon: CloudIcon
  }
};

const ImageList = ({
  onClose,
  handleDraw,
  handleUpload,
  geometry,
  onMouseEnter,
  onMouseLeave,
  onAddImage,
  onRemoveImage
}) => {
  const dispatch = useDispatch();
  const scrollViewRef = useRef(null);

  const { activeOrg } = useContext(IdentityContext);
  const tiles = useSelector(state => state.map.selectedImages);
  const filters = useSelector(state => state.filters);
  const { constellations } = useConstellations();
  const debounceFilters = useDebounce(filters, 500);

  const location = useLocation();

  // local states
  const [showFilters, setShowFilters] = useState(
    location?.state?.showNewFilter || false
  );
  const [scene, setScene] = useState(null);
  const [selectedSort, setSelectedSort] = useState({});
  const [sortOrder, SetSortOrder] = useState('descend');
  const [images, setImages] = useState([]);
  const [content, setContent] = useState({
    posts: [],
    postesPerPage: 20
  });
  const [pageNumbers, setPageNumbers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showSelected, setShowSelected] = useState(false);

  // useEffect(() => console.log("content:", content), [content]);
  // useEffect(() => console.log("selectedSort:", selectedSort), [selectedSort]);
  // useEffect(() => console.log('images:', images), [images]);
  // useEffect(() => console.log("sortOrder:", sortOrder), [sortOrder]);

  function handleUpdateContent(newContent) {
    setContent(newContent);
    scrollViewRef.current?.scrollIntoView();
  }

  const pricingAborter = useRef(null);

  // Fetch Images Hook
  const {
    data: imageList,
    trigger: SearchTrigger,
    isMutating: ImgSearching
  } = useSWRMutation(['/search', activeOrg], FetchImages, {
    onSuccess: data => {
      ImageSearchHelper(
        data,
        setImages,
        content,
        handleUpdateContent,
        sortOrder,
        currentPage,
        setCurrentPage,
        setPageNumbers,
        constellations
      );
    },
    onError: LensErrorHelper
  });

  const [prices, setPrices] = useState({});
  const [isPricingLoading, setIsPricingLoading] = useState(false);

  const handleGetPricing = useCallback(
    async imageIds => {
      try {
        setIsPricingLoading(true);

        pricingAborter.current = new AbortController();

        const res = await apiClient.clerkApi.v1PricingCreate(
          { image_ids: imageIds },
          { signal: pricingAborter.signal }
        );

        setPrices(res.data.prices);
      } catch (err) {
        console.error(err);
      } finally {
        setIsPricingLoading(false);
      }
    },
    [pricingAborter]
  );

  useEffect(() => {
    pricingAborter.current?.abort();

    const imageIds = [];

    for (const x of Object.entries(imageList ?? {})) {
      if (!x[1].is_accessible) imageIds.push(x[0]);
    }

    if (imageIds.length > 0) handleGetPricing(imageIds);
  }, [handleGetPricing, imageList, pricingAborter]);

  function handleSelect(image) {
    // console.log(image);
    // if tiles are already present
    const newTile = {
      id: image.id,
      image_id: image.id,
      preview_url: image.url,
      stac_url: image.stac_url,
      geometry: image.geometry,
      datetime: image.datetime,
      satellite: image.satellite,
      area: image.area,
      cloud: image.cloud,
      sun_elevation: image.sun_elevation,
      satellite_look_angle: image.satellite_look_angle,
      off_nadir: image.off_nadir,
      sun_azimuth: image.sun_azimuth,
      bands: image.bands,
      collection_id: image.collection_id,
      constellation_id: image.constellation_id,
      provider: image.provider,
      is_accessible: image.is_accessible,
      name:
        constellations?.find(d => d.id === image.constellation_id)?.name || '',
      url: image.preview_url,
      gsd: image.gsd
    };

    if (tiles.length > 0) {
      // if selected tiles is already present, then remove it
      if (tiles.some(tile => tile.id === image.id)) {
        let filteredTiles = tiles.filter(i => i.id !== image.id);
        dispatch(setSelectedImages(filteredTiles));
        onRemoveImage(image.id);
        return;
      }
      // if selected image is not present then insert

      dispatch(setSelectedImages([...tiles, newTile]));
      onAddImage(newTile);

      return;
    }

    // Add image directly when no tile present
    dispatch(setSelectedImages([newTile]));
    onAddImage(newTile);
  }

  const isEmptyGeometry = useMemo(() => _.isEmpty(geometry), [geometry]);

  useEffect(() => {
    if (!isEmptyGeometry) setShowFilters(true);
  }, []);

  // each time geometry is updated, fetch images api to be called
  useEffect(() => {
    if (!isEmptyGeometry && filters.satellite?.length > 0) {
      RunImageSearch(SearchTrigger, geometry);
      // // remove images from selectedImages that do not fall in filter date range
      // let newTileArray = [];
      // tiles.map(tile => {
      //   if (
      //     moment
      //       .utc(tile.datetime)
      //       .isBetween(filters.startDate, filters.endDate)
      //   ) {
      //     newTileArray.push(tile);
      //   }
      // });
      dispatch(setSelectedImages(tiles));
      return;
    }
    handleUpdateContent({ posts: [], postesPerPage: 20 });
    setCurrentPage(1);
    setImages([]);
    dispatch(setSelectedImages([]));
  }, [
    isEmptyGeometry,
    geometry,
    filters.startDate,
    filters.endDate,
    filters.satellite,
    debounceFilters.areaCoverage,
    debounceFilters.cloudCoverage,
    debounceFilters.spatialResolution,
    debounceFilters.offNadirAngle,
    filters.filterStates,
    activeOrg.id,
    dispatch,
    SearchTrigger
  ]);

  // update page values
  useEffect(() => {
    let array = images;
    let IndLastPost = currentPage * content.postesPerPage;
    let IndFistPost = IndLastPost - content.postesPerPage;
    handleUpdateContent({
      posts: array.slice(IndFistPost, IndLastPost),
      postesPerPage: content.postesPerPage
    });
  }, [currentPage, images]);

  //Set current page
  const setPage = pageNum => {
    if (pageNum <= pageNumbers.length && pageNum >= 1) {
      setCurrentPage(pageNum);
    }
  };

  const sortImages = useCallback(
    (images = [], updateImages, key) => {
      let imageListClone = [..._.cloneDeep(images)];
      if (key === 'date') {
        if (sortOrder === 'descend') {
          SetSortOrder('ascend');
          imageListClone.sort(
            (a, b) =>
              new moment.utc(a.datetime).format('YYYYMMDD') -
              new moment.utc(b.datetime).format('YYYYMMDD')
          );
        }
        if (sortOrder === 'ascend') {
          SetSortOrder('descend');
          imageListClone.sort(
            (a, b) =>
              new moment.utc(b.datetime).format('YYYYMMDD') -
              new moment.utc(a.datetime).format('YYYYMMDD')
          );
        }
      }
      if (key === 'satellite') {
        if (sortOrder === 'descend') {
          SetSortOrder('ascend');
          imageListClone.sort((a, b) =>
            a.satellite.toLowerCase().localeCompare(b.satellite.toLowerCase())
          );
        }
        if (sortOrder === 'ascend') {
          SetSortOrder('descend');
          imageListClone.sort((a, b) =>
            b.satellite.toLowerCase().localeCompare(a.satellite.toLowerCase())
          );
        }
      }
      if (key === 'cloud') {
        if (sortOrder === 'descend') {
          SetSortOrder('ascend');
          imageListClone.sort((a, b) => a.cloud - b.cloud);
        }
        if (sortOrder === 'ascend') {
          SetSortOrder('descend');
          imageListClone.sort((a, b) => b.cloud - a.cloud);
        }
      }

      updateImages(imageListClone);

      return;
    },
    [sortOrder]
  );

  const updateSourceImages = images => {
    if (showSelected && tiles.length > 0) {
      dispatch(setSelectedImages(images));
      return;
    }

    setImages(images);
  };

  const handleSort = option => {
    setSelectedSort({
      ...sortOptions[option],
      key: option
    });

    const imagesResult = showSelected && tiles.length > 0 ? tiles : images;

    sortImages(imagesResult, updateSourceImages, option);
  };

  const tabsValue = showSelected ? TABS.SELECTED : TABS.ALL;

  const isLoading = ImgSearching || isPricingLoading;

  return (
    <Flex gap={3} className={styles.ImageListWrapper}>
      <NewFilter
        setShowFilters={setShowFilters}
        showFilters={showFilters && !scene}
        images={images}
      />

      <Show when={scene}>
        <ScenesInfo
          title='Image info'
          selectedSceneInfo={scene}
          onClose={() => setScene(null)}
          selector={{
            getDate: d => (
              <>
                {getFormattedUtcDateTime(d.datetime)}{' '}
                <Text variant='secondary' weight={500}>
                  (UTC)
                </Text>
              </>
            ),
            getBandsLen: d => <BandsList bands={d.bands} />,
            getSatellite: d => d.satellite,
            getCloudCoverage: d =>
              typeof d.cloud === 'number' && d.cloud > -1
                ? `${d.cloud?.toFixed(2) ?? 0}%`
                : 'N/A',
            getImageId: d => d.id,
            getCollection: d => d.name || 'N/A',
            getAzimuthAngle: d => getAngleValue(d['sun_azimuth']),
            getSunElevation: d => getAngleValue(d['sun_elevation']),
            getLookAngle: d => getAngleValue(d['off_nadir']),
            getGsd: d => (d.gsd || d.gsd === 0 ? `${d.gsd} m` : 'N/A')
          }}
        />
      </Show>

      <div className={styles.ImageDiv}>
        <Header onClose={onClose}>
          <Header.Text size='medium'>Images</Header.Text>
        </Header>

        <div className={styles.subHeader}>
          <Tabs defaultValue={tabsValue}>
            <Tabs.List className={styles.tabsWrapper}>
              <Tabs.Trigger
                data-test-id='filters-all-btn'
                value={TABS.ALL}
                style={{ width: '100px' }}
                onClick={() => setShowSelected(false)}
              >
                All
                <span
                  style={{
                    color: 'var(--rs-color-foreground-base-secondary)',
                    marginLeft: 'var(--rs-space-2)'
                  }}
                >
                  ({images.length ? images.length : 0})
                </span>
              </Tabs.Trigger>

              <Tabs.Trigger
                data-test-id='selected-all-btn'
                value={TABS.SELECTED}
                style={{ width: '100px' }}
                onClick={() => setShowSelected(true)}
              >
                Selected
                <span
                  style={{
                    color: 'var(--rs-color-foreground-base-secondary)',
                    marginLeft: 'var(--rs-space-2)'
                  }}
                >
                  ({tiles ? tiles.length : 0})
                </span>
              </Tabs.Trigger>
            </Tabs.List>
          </Tabs>

          <Flex align='center'>
            <Button
              data-test-id='toggle-filters-btn'
              variant='text'
              color='neutral'
              onClick={() => {
                if (scene) {
                  setScene(null);
                  setShowFilters(true);
                } else {
                  setShowFilters(!showFilters);
                }
              }}
              size='small'
            >
              <NewFilterIcon className={styles.actionIcon} />
            </Button>
            <DropdownMenu placement='bottom-end'>
              <DropdownMenu.Trigger
                disabled={content.posts.length === 0}
                asChild
              >
                <Button
                  data-test-id='toggle-sort-btn'
                  variant='text'
                  color='neutral'
                  disabled={content.posts.length === 0}
                  size='small'
                >
                  <MixerHorizontalIcon className={styles.actionIcon} />
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content style={{ padding: 0 }}>
                <DropdownMenu.Group>
                  {_.keys(sortOptions).map((option, i) => {
                    const Icon = sortOptions[option].icon;
                    const currentSelection =
                      !_.isEmpty(selectedSort) &&
                      selectedSort.name === sortOptions[option].name;
                    return (
                      <DropdownMenu.Item
                        data-test-id={sortOptions[option].name}
                        key={i}
                        onClick={() => handleSort(option)}
                      >
                        <div className={styles.SortItemWrapper}>
                          <div className={styles.sortItemTitle}>
                            <Icon className={styles.sortItemIcon} />
                            <Text variant='secondary'>
                              {sortOptions[option].name}
                            </Text>
                          </div>

                          {currentSelection && sortOrder === 'descend' && (
                            <ArrowDownIcon />
                          )}
                          {currentSelection && sortOrder === 'ascend' && (
                            <ArrowUpIcon />
                          )}
                        </div>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Group>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Flex>
        </div>

        <ScrollView className={styles.ImageResultWrapper} ref={scrollViewRef}>
          {isLoading ? (
            [...Array(3)].map(i => {
              return (
                <div key={i}>
                  <Skeleton.TileList />
                </div>
              );
            })
          ) : (
            <NewTileView
              aoiGeometry={geometry}
              prices={prices}
              content={showSelected ? tiles : content.posts}
              tiles={tiles}
              handleSelect={handleSelect}
              showSelected={showSelected}
              handleDrawing={handleDraw}
              handleUpload={handleUpload}
              onViewMoreClick={image => {
                if (!scene || scene.id !== image.id) {
                  setScene(image);
                } else {
                  setScene(null);
                }
              }}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            />
          )}
        </ScrollView>

        {/* pagination strip */}
        {!showSelected &&
          content.posts.length !== 0 &&
          pageNumbers.length > 1 && (
            <div className={styles.footerWrapper}>
              <div className={styles.footer}>
                <IconButton
                  data-test-id='chevron-left-btn'
                  onClick={() => setPage(currentPage - 1)}
                >
                  <ChevronLeftIcon />
                </IconButton>
                <div style={{ justifyContent: 'center', alignSelf: 'center' }}>
                  <Label size='medium'>
                    {currentPage}/{pageNumbers.length}
                  </Label>
                </div>

                <IconButton
                  data-test-id='chevron-right-btn'
                  onClick={() => setPage(currentPage + 1)}
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            </div>
          )}
      </div>
    </Flex>
  );
};

const mapStateToProps = state => ({
  selectedImages: state.map.selectedImages
});
export default connect(mapStateToProps)(ImageList);
